import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import ScrollTopButton from '../components/ScrollTopButton';

/**
 * /terms — Marakin Terms of Service (GDPR Week 6 remainder).
 *
 * DRAFT — entity details filled 7 Jul 2026 (Marakin Ltd, 17325460). Remaining
 * before final: R-5 full legal review. Mirrors the structure and tone of
 * PrivacyPolicyPage. Bump TERMS_VERSION when the text changes materially.
 */

// Keep in sync with backend/src/lib/terms.js (TERMS_VERSION) — that constant
// is what terms_acceptances rows are stamped with (v33 clickwrap).
const TERMS_VERSION = '2026-08-28';
// 18 Oct 2026 (WT-29b): the sponsor sentence now matches the privacy policy.
// TERMS_VERSION not bumped (a factual correction; it only stamps new acceptances).
const LAST_UPDATED = '18 October 2026';

const S = ({ title, children }) => (
  <section style={{ marginBottom: 28 }}>
    <h2 style={{ fontSize: 20, marginBottom: 10 }}>{title}</h2>
    {children}
  </section>
);

const P = ({ children }) => (
  <p style={{ fontSize: 15, lineHeight: 1.65, color: 'var(--text-primary)', marginBottom: 10 }}>{children}</p>
);

export default function TermsPage() {
  const navigate = useNavigate();
  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate('/'));
  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)' }}>
      <ScrollTopButton />
      <div style={{ maxWidth: 760, margin: '0 auto', padding: 'calc(40px + var(--sat)) 20px 80px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <button type="button" onClick={goBack}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-secondary)', fontFamily: 'Nunito, sans-serif', fontSize: 14, fontWeight: 700 }}>
            <ChevronLeft size={16} /> Back to club hub
          </button>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center' }}>
            <img src="/marakin-logo-charcoal.png" alt="Marakin" style={{ height: 22 }} />
          </Link>
        </div>

        <h1 style={{ fontSize: 30, marginBottom: 6 }}>Terms of Service</h1>
        <p style={{ fontSize: 13.5, color: 'var(--text-muted)', marginBottom: 32 }}>
          Version {TERMS_VERSION} · Last updated {LAST_UPDATED} · DRAFT for pilot
        </p>

        <S title="Who we are">
          <P>
            Marakin ("we", "us") provides a digital platform that grassroots sports clubs use to
            manage members, teams, events, communications, kit and sponsorship. These terms are a
            contract between you and <strong>Marakin Ltd</strong>, a company registered in
            England and Wales (company number <strong>17325460</strong>), registered office
            <strong> 2 Alison Gardens, Backwell, Bristol, BS48 3LT</strong>. Contact:
            <strong> privacy@marakin.co.uk</strong>.
          </P>
          <P>
            By creating an account or using a club's Marakin hub, you agree to these terms. How we
            handle your personal data is covered separately in our{' '}
            <Link to="/privacy" style={{ textDecoration: 'underline' }}>privacy policy</Link>.
          </P>
        </S>

        <S title="The service — and its pilot status">
          <P>
            Marakin is currently in a <strong>pilot phase</strong>. The service is provided free
            of charge to members during the pilot, features may change, be added or be withdrawn,
            and occasional interruptions are more likely than in a finished product. We'll do our
            best to keep things running and to warn of planned downtime, but we don't guarantee
            uninterrupted availability during the pilot.
          </P>
        </S>

        <S title="Your account">
          <P>
            Accounts are for adults aged <strong>18 and over</strong>. Parents and guardians add
            children and under-18s to their own account and manage everything on their behalf —
            under-18s never get their own login.
          </P>
          <P>
            You agree to give accurate information when registering, to keep your password secret,
            and to tell us promptly if you think someone else has used your account. You're
            responsible for activity that happens under your login.
          </P>
        </S>

        <S title="Your content and behaviour">
          <P>
            Marakin includes member-to-member features such as club chat and event discussions.
            You keep ownership of anything you post, and you give us the permission we need to
            store and display it to the right people in your club so the service works.
          </P>
          <P>
            Don't post anything unlawful, abusive, discriminatory, threatening, sexually explicit,
            or that harasses or bullies another person — remember that children are present in
            club spaces. Don't spam, impersonate others, or share other people's personal
            information without their agreement.
          </P>
          <P>
            Any member can report a message using the flag icon. Club admins and Marakin can
            remove content and suspend or close accounts that break these rules — immediately and
            without notice where children's safety is involved.
          </P>
        </S>

        <S title="Your club's role">
          <P>
            Each club hub is run by the club itself. Club admins decide who joins, what's posted
            in announcements, which events exist and how teams are organised. Your club's own
            rules, codes of conduct and safeguarding policies continue to apply alongside these
            terms. Marakin provides the platform; we're not a party to the relationship between
            you and your club.
          </P>
        </S>

        <S title="Sponsor offers and QR redemptions">
          <P>
            Clubs' sponsors can publish offers that members redeem by QR code. Each offer is made
            by the sponsor, not by Marakin — the sponsor is responsible for honouring it, and any
            terms attached to an offer (dates, limits, eligibility) are the sponsor's. When you
            redeem an offer the sponsor sees your name, the offer and what you spent, as described
            in the privacy policy; we don't guarantee any particular offer remains available.
          </P>
        </S>

        <S title="Tickets, kit and third-party services">
          <P>
            Some events link to external ticketing providers, and kit purchases may be fulfilled
            by your club's chosen supplier. Those purchases happen under the third party's or the
            club's own terms, not these — check theirs before you buy. We're not responsible for
            third-party websites or services we link to.
          </P>
        </S>

        <S title="Our content">
          <P>
            The Marakin platform — its software, design, logos and branding — belongs to us or
            our licensors. Club badges, sponsor logos and member content belong to their
            respective owners. You may not copy, scrape, resell or reverse-engineer the platform.
          </P>
        </S>

        <S title="Liability">
          <P>
            Nothing in these terms limits liability that can't legally be limited — including for
            death or personal injury caused by negligence, or fraud. Beyond that, because the
            service is provided free during the pilot, we're not liable for indirect losses, lost
            data caused by events outside our reasonable control, or the acts of clubs, sponsors
            and other members. Where we are liable, our total liability is limited to £100.
          </P>
          <P>
            Marakin is a club-organisation tool. Information in the app (for example "leave-by"
            journey times or weather) is provided for convenience and shouldn't be your only
            source for safety-critical decisions.
          </P>
        </S>

        <S title="Ending things">
          <P>
            You can stop using Marakin and delete your account at any time in{' '}
            <strong>My account → Privacy &amp; data</strong>, or without logging in at{' '}
            <Link to="/delete-account" style={{ textDecoration: 'underline' }}>the account deletion page</Link>.
            We can suspend or close accounts that break these terms, and may withdraw the service
            at the end of the pilot with reasonable notice. What happens to your data on deletion
            is described in the privacy policy.
          </P>
        </S>

        <S title="Changes to these terms">
          <P>
            We may update these terms as the service evolves — particularly when the pilot ends.
            We'll notify you in-app of material changes, and the version number above tells you
            which text applies. Continuing to use Marakin after a change means you accept the
            updated terms.
          </P>
        </S>

        <S title="Law and disputes">
          <P>
            These terms are governed by the law of England and Wales, and the courts of England
            and Wales have jurisdiction (if you live in Scotland or Northern Ireland you can also
            use your local courts). If any part of these terms turns out to be unenforceable, the
            rest still applies.
          </P>
        </S>

        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 40 }}>
          Marakin · Powering community sport ·{' '}
          <Link to="/privacy" style={{ textDecoration: 'underline' }}>privacy policy</Link> ·{' '}
          <Link to="/" style={{ textDecoration: 'underline' }}>marakin home</Link>
        </p>
      </div>
    </div>
  );
}
